import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { ProposalEntity } from 'v2/models';
import DataRequestItem from '../DataRequestItem/DataRequestItem';
import styles from './closedDataRequests.module.scss';

export interface ClosedDataRequestsProps {
  closedApproved: ProposalEntity[];
  closedRejected: ProposalEntity[];
  closedDeclined: ProposalEntity[];
  closedCancelled: ProposalEntity[];
}

export type ClosedRequestsTabType = {
  eventKey: 'approved' | 'rejected' | 'declined' | 'cancelled';
  title: string;
  list: ProposalEntity[];
  subtitle: string;
};

const ClosedDataRequests = ({
  closedApproved,
  closedRejected,
  closedDeclined,
  closedCancelled,
}: ClosedDataRequestsProps) => {
  const TABS: ClosedRequestsTabType[] = [
    {
      eventKey: 'approved',
      title: `approved`,
      list: closedApproved,
      subtitle:
        // eslint-disable-next-line max-len
        'These are all the requests where the buyer had approved your request and then later marked the request as complete.',
    },
    {
      eventKey: 'rejected',
      title: `rejected`,
      list: closedRejected,
      subtitle:
        'These are all the requests where the buyer had rejected your response to their request.',
    },
    {
      eventKey: 'declined',
      title: `you declined`,
      list: closedDeclined,
      subtitle:
        "These are all the requests that you had indicated weren't a good fit for your business.",
    },
    {
      eventKey: 'cancelled',
      title: `cancelled`,
      list: closedCancelled,
      subtitle:
        'These are all the requests that the buyer closed before they were responded to.',
    },
  ];

  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const [selectedTabUrl, setSelectedTabUrl] = useState(
    pathname.split('/')?.[3]
  );
  const defaultTab = TABS[0].eventKey;
  const [key, setKey] = useState<string>(selectedTabUrl || defaultTab);
  const history = useHistory();

  useEffect(() => {
    setSelectedTabUrl(pathname);

    const newKey = pathname.split('/')?.[3];
    if (newKey && newKey !== key) {
      setKey(newKey);
    }
  }, [pathname, key]);

  return (
    <div
      className={classnames(
        'container-xl',
        'fix-bg-images',
        'h-100',
        styles.root
      )}
    >
      <Tabs
        className={classnames('border-bottom-0')}
        activeKey={key}
        onSelect={(key) => {
          setKey(key || defaultTab);
          history.push(`${path}/${key}`);
        }}
        role="tab"
        transition={false}
      >
        {TABS.map((item) => (
          <Tab
            key={item.eventKey}
            className="border-primary"
            eventKey={item.eventKey}
            unmountOnExit={true}
            title={`${item.title} (${item.list.length})`}
          >
            <div className={classnames('pt-3 h-100 position-relative')}>
              <Switch>
                {TABS.map((tab) => (
                  <Route
                    key={tab.eventKey}
                    path={`${path}/${String(tab.eventKey)}`}
                  >
                    {item.list.length ? (
                      <>
                        <h5
                          className={classnames(
                            'text-primary-dark mt-5 mb-4',
                            styles.description
                          )}
                        >
                          {tab.subtitle}
                        </h5>
                        {item.list.map((proposal) => (
                          <DataRequestItem
                            key={proposal.id}
                            tabKey={tab.eventKey}
                            className="mb-3"
                            proposal={proposal}
                          />
                        ))}
                      </>
                    ) : (
                      <h5
                        className={classnames(
                          'text-primary-dark mt-5 mb-4',
                          styles.description
                        )}
                      >
                        {item.eventKey === 'approved' ||
                        item.eventKey === 'rejected' ||
                        item.eventKey === 'cancelled'
                          ? `There are no ${item.title} requests.`
                          : `There are no requests ${item.title}.`}
                      </h5>
                    )}
                  </Route>
                ))}
              </Switch>
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

export default ClosedDataRequests;
